import { Item } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import {
  A3InnuvaJobPositionDocument,
  A3innuvaContractsJobPositionFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import DropdownSearchBar from 'modules/core/components/DropdownSearchBar'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { SpanishContractData } from 'modules/contracts/hooks/useFetchSpanishContractData'
import { translate } from 'modules/contracts/lib/translate'
import {
  fullName,
  jobPositionsToOptions,
} from 'modules/payroll_integrations_a3innuva/lib/a3innuvaHelpers'

interface Props {
  form: Form
  spanishContractData: SpanishContractData
  translations: { [key: string]: string }
  legalEntityId: number
}

const A3innuvaJobPosition = ({ form, spanishContractData, translations, legalEntityId }: Props) => {
  const session = useCurrentSession()
  const jobPositionName = form.get('es_a3innuva_job_position_payroll_integration_name').value
  const jobPositionCode = form.get('es_a3innuva_job_position_payroll_integration_code').value
  const jobPositionId = form.get('es_a3innuva_job_position_id').value
  const spainJobPosition = jobPositionId
    ? spanishContractData.a3innuvaJobPositions.get(Number(jobPositionId))
    : null

  const setCurrentJobPosition = (a3JobPositions: A3innuvaContractsJobPositionFragment[]) => {
    const currentJobPosition = a3JobPositions.find(
      ({ name }) => name === spainJobPosition?.attributes.name
    )

    if (currentJobPosition) {
      form.setValues({
        es_a3innuva_job_position_payroll_integration_code: currentJobPosition.code,
        es_a3innuva_job_position_payroll_integration_name: currentJobPosition.name,
      })
    }
  }

  const isFormInitialized = () => {
    if (!form.has('es_a3innuva_job_position_payroll_integration_name')) return false
    return (
      form.get('es_a3innuva_job_position_payroll_integration_name').value ===
      spainJobPosition?.attributes.name
    )
  }

  const { data: jobPositionStepData, loading } = useQuery(A3InnuvaJobPositionDocument, {
    variables: {
      companyId: session.company.id,
      currentUserId: session.access.userId,
      legalEntityId,
    },
  })

  const a3innuvaJobPositions = jobPositionStepData?.payrollIntegrationsA3innuva.jobPositions ?? []

  if (!loading && !isFormInitialized()) {
    setCurrentJobPosition(a3innuvaJobPositions)
  }

  const options = jobPositionsToOptions(a3innuvaJobPositions)

  const onJobPositionChanged = (option: Item) => {
    const selectedJobPosition = a3innuvaJobPositions.find(({ code }) => code === option.value)
    form.setValues({
      es_a3innuva_job_position_payroll_integration_name: selectedJobPosition?.name,
      es_a3innuva_job_position_payroll_integration_code: selectedJobPosition?.code,
      es_a3innuva_job_position_id: undefined,
    })
  }

  const labelNotFound = () => {
    return options.length
      ? i18n.t('contract_versions.forms.contract_extra_information.a3innuva.job_position_not_found')
      : i18n.t('contract_versions.forms.contract_extra_information.a3innuva.label_not_found')
  }

  return (
    <DropdownSearchBar
      name={fullName(jobPositionCode, jobPositionName)}
      label={translate(translations, 'a3innuva_job_position') || ''}
      onSelect={onJobPositionChanged}
      labelNotFound={labelNotFound()}
      data={options}
      searchLabel={i18n.t(
        'contract_versions.forms.contract_extra_information.a3innuva.search_label'
      )}
      loading={loading}
      popoverWidth="s400"
      ellipsis={false}
      disabled
    />
  )
}

export default observer(A3innuvaJobPosition)
